<template>
  <div class="tinymce-editor">
    <textarea :id="editorId"></textarea>
  </div>
</template>

<script>
import tinymce from 'tinymce/tinymce';

// --- 核心 ---
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

import http from '../services/http';

export default {
  props: {
    value: {
      type: [String, Array],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    plugins: {
      type: [String, Array],
    },
    toolbar: {
      type: [String, Array],
    },
    height: {
      type: Number,
      default: 300,
    },
    fontsizeFormats: {
      type: String,
      default: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
    },
    link_assume_external_targets: {
      type: [String, Boolean],
      default: true,
    },
    content_langs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      editorId: `tinymce-editor-${Math.random().toString(36).substring(7)}`,
      editorInstance: null,
    };
  },

  mounted() {
    const tinymceAssetPath = '/tinymce/';
    // const tinymceKey = 'i938hi6ii2kt8w58qsozf3wrw5dz7m9ji7mnz9lwqbjep6vb';
    const defaultPlugins = 'advlist autolink lists link image charmap preview anchor '
                           + 'searchreplace visualblocks code '
                           + 'insertdatetime media table help wordcount';

    let finalPlugins = defaultPlugins;

    if (this.plugins) {
      if (typeof this.plugins === 'string') {
        finalPlugins = this.plugins;
      } else if (Array.isArray(this.plugins)) {
        finalPlugins = this.plugins.join(' ');
      }
    }

    let finalToolbar = 'undo redo | formatselect fontsize | bold italic backcolor | '
                       + 'alignleft aligncenter alignright justify | '
                       + 'bullist numlist outdent indent | removeformat | link image | code';

    if (this.toolbar && typeof this.toolbar === 'string') {
      finalToolbar = this.toolbar;
    }

    const initOptions = {
      selector: `#${this.editorId}`,
      // api_key: tinymceKey,
      height: this.height,

      plugins: finalPlugins,
      toolbar: finalToolbar,

      browser_spellcheck: true,
      contextmenu: false,
      fontsize_formats: this.fontsizeFormats,
      link_assume_external_targets: this.link_assume_external_targets,
      content_langs: this.content_langs,

      branding: false,
      menubar: false,
      relative_urls: false,
      remove_script_host: false,

      base_url: tinymceAssetPath,
      skin_url: `${tinymceAssetPath}skins/ui/oxide`,
      content_css: `${tinymceAssetPath}skins/content/default/content.min.css`,
      suffix: '.min',

      automatic_uploads: true,
      file_picker_types: 'image',
      file_picker_callback: (cb) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.addEventListener('change', (e) => {
          const file = e.target.files[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);

              const supportsAvif = document.createElement('canvas').toDataURL('image/avif').indexOf('data:image/avif') === 0;
              const targetFormat = supportsAvif ? 'image/avif' : 'image/webp';
              const ext = supportsAvif ? 'avif' : 'webp';

              canvas.toBlob((blob) => {
                this.saveStatToDB({
                  filename: file.name,
                  originalSize: file.size,
                  compressedSize: blob.size,
                  ratio: ((blob.size / file.size) * 100).toFixed(2),
                  format: ext,
                  timestamp: new Date().toISOString(),
                });
                const blobReader = new FileReader();
                blobReader.onloadend = () => {
                  const [header, base64data] = blobReader.result.split(',');
                  // 為了避開 eslint 對 unused header 的檢查，這裡雖然解構了但其實只用第二個
                  if (header) { /* placeholder */ }

                  // const blobCache = this.editorInstance.editorUpload.blobCache;
                  const { blobCache } = this.editorInstance.editorUpload;
                  const id = `blobid${(new Date()).getTime()}`; // 修正：模板字串
                  const blobInfo = blobCache.create(id, blob, base64data);
                  blobCache.add(blobInfo);
                  cb(blobInfo.blobUri(), { title: file.name });
                };
                blobReader.readAsDataURL(blob);
              }, targetFormat, 0.8);
            };
            img.src = reader.result;
          };
          reader.readAsDataURL(file);
        });
        input.click();
      },

      setup: (editor) => {
        this.editorInstance = editor;
        editor.on('init', () => {
          let safeContent = '';
          if (this.value) {
            if (Array.isArray(this.value)) {
              // 如果是空陣列 []，視為空字串
              safeContent = '';
            } else {
              // 強制轉字串
              safeContent = String(this.value);
            }
          }
          editor.setContent(safeContent);
          editor.mode.set(this.disabled ? 'readonly' : 'design');
        });
        editor.on('input change undo redo', () => {
          const content = editor.getContent();
          const currentVal = Array.isArray(this.value) ? '' : (this.value || '');
          if (currentVal !== content) {
            this.$emit('input', content);
          }
        });
      },
    };

    tinymce.init(initOptions);
  },

  beforeDestroy() {
    if (this.editorInstance) {
      this.editorInstance.destroy();
      this.editorInstance = null;
    }
  },

  watch: {
    value(newValue) {
      let safeContent = '';
      if (newValue) {
        if (Array.isArray(newValue)) {
          safeContent = '';
        } else {
          safeContent = String(newValue);
        }
      }
      if (this.editorInstance && safeContent !== this.editorInstance.getContent()) {
        this.editorInstance.setContent(safeContent);
      }
    },
    disabled(newValue) {
      if (this.editorInstance) {
        this.editorInstance.mode.set(newValue ? 'readonly' : 'design');
      }
    },
  },

  methods: {
    saveStatToDB(stat) {
      http('/upload-stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stat),
      }).then((res) => {
        if (res.ok) {
          // console.log('統計已儲存到資料庫', res);
        } else {
          // console.error('儲存失敗', res);
        }
      });
    },
  },
};
</script>
